import { useState, useRef, useEffect } from 'react'
import { postDataAPI } from '../../../lib/api'
import { uploadImage } from '../../../lib/uploadImage'
import { trackSignup, setGA4UserId, setGA4UserProperties } from '../../../lib/analytics'
import OnboardingShell from './OnboardingShell'
import type { SignupUser } from '../SignupPage'
import TextField from '../../../components/ui/TextField'
import { OnboardingStep } from './steps'

const USERNAME_REGEX = /^[a-z0-9_-]+$/
const deriveUsername = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 30)

export default function Step08TeamUsername({
  setStep,
  user,
  setUser,
}: {
  setStep: (n: number) => void
  user: SignupUser & { availableArtists?: any[] }
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
}) {
  const [name, setName] = useState(user.name || '')
  const [username, setUsername] = useState(() => deriveUsername(user.name || ''))
  const [usernameEdited, setUsernameEdited] = useState(false)
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [avatar, setAvatar] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => { if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current) }
  }, [])

  const checkAvailability = (val: string) => {
    if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current)
    if (val.length < 2) {
      setUsernameStatus('idle')
      if (val.length > 0) setUsernameError('Must be at least 2 characters')
      return
    }
    if (!USERNAME_REGEX.test(val)) {
      setUsernameStatus('idle')
      setUsernameError('Only lowercase letters, numbers, hyphens, and underscores')
      return
    }
    setUsernameStatus('checking')
    setUsernameError(null)
    checkTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await postDataAPI('auth/checkUserName', { username: val })
        const exists = !!res?.data?.exist
        setUsernameStatus(exists ? 'taken' : 'available')
        if (exists) setUsernameError('This username is already taken')
      } catch {
        setUsernameStatus('idle')
        setUsernameError('Failed to check availability')
      }
    }, 300)
  }

  const handleNameChange = (val: string) => {
    setName(val)
    if (!usernameEdited) {
      const derived = deriveUsername(val)
      setUsername(derived)
      if (derived.length >= 2) checkAvailability(derived)
      else { setUsernameStatus('idle'); setUsernameError(null) }
    }
  }

  const handleUsernameChange = (raw: string) => {
    const val = raw.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 30)
    setUsername(val)
    setUsernameEdited(true)
    setUsernameError(null)
    if (val.length >= 2) checkAvailability(val)
    else {
      setUsernameStatus('idle')
      if (val.length > 0) setUsernameError('Must be at least 2 characters')
    }
  }

  const isUsernameValid = username.length >= 2 && USERNAME_REGEX.test(username) && usernameStatus === 'available'

  const role = user.professionRole === 'manager' || user.professionRole === 'support' || user.professionRole === 'promoter' ? user.professionRole : 'support'
  const roleLabel = role === 'manager' ? 'Member' : role === 'promoter' ? 'Promoter' : 'Support'

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setAvatar(file)
      const reader = new FileReader()
      reader.onloadend = () => setAvatarPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const createAccountAndNavigate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user.email || !name.trim()) return
    setError(null)
    setIsCreating(true)
    try {
      let avatarUrl = null
      if (avatar) {
        try {
          avatarUrl = await uploadImage(avatar)
        } catch (err) {
          console.error('Avatar upload failed:', err)
          // Continue without avatar
        }
      }

      const userToSubmit: any = {
        email: user.email,
        name: name.trim(),
        username,
        professionRole: role,
        teamMemberPlan: user.teamMemberPlan || (role === 'manager' || role === 'promoter' ? 'basic' : 'support'),
        cover: null,
        avatar: avatarUrl,
      }

      const createRes = await postDataAPI('auth/add_user_info', userToSubmit)
      if (createRes?.status !== 200) throw new Error(createRes?.message || 'Failed to create account')

      const createdUser = createRes?.data?.user
      const token = createRes?.data?.token
      if (token) localStorage.setItem('dizeeToken', JSON.stringify(token))
      if (createdUser) localStorage.setItem('dizeeUser', JSON.stringify(createdUser))
      try {
        localStorage.setItem('isNewSignup', 'true')
      } catch {
        /* ignore */
      }
      trackSignup('email')
      if (createdUser?._id) setGA4UserId(createdUser._id)
      setGA4UserProperties({ user_role: role, signup_method: 'email', has_team: 'true' })

      setUser((prev) => ({ ...prev, name: name.trim(), avatar: avatarUrl }))
      // Join teams after the user exists (join-artist looks up User by email).
      sessionStorage.setItem('signupTeamJoinPending', '1')
      setStep(OnboardingStep.TeamJoin)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create account')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <OnboardingShell
      title="What should we call you?"
      subtitle={`You're joining as a ${roleLabel}`}
      footer="This will be your display name when working with teams."
    >
      <form onSubmit={createAccountAndNavigate} className="space-y-5">
        {/* Avatar Upload */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div
              className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden"
              style={{ background: avatarPreview ? 'transparent' : 'linear-gradient(45deg, #6366f1, #8b5cf6)' }}
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-2xl font-semibold">
                  {name ? name.charAt(0).toUpperCase() : '?'}
                </span>
              )}
            </div>
            <label
              htmlFor="avatar-upload"
              className="absolute bottom-0 right-0 w-7 h-7 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </label>
            <input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Optional profile picture</p>
        </div>

        <TextField value={name} onChange={handleNameChange} placeholder="Enter your name" error={error} />

        <div>
          <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">Username</label>
          <div className="relative">
            <input
              type="text"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              placeholder="username"
              maxLength={30}
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent ${
                usernameError ? 'border-red-300 dark:border-red-500' : usernameStatus === 'available' ? 'border-green-300 dark:border-green-500' : 'border-gray-200 dark:border-gray-600'
              }`}
            />
            {usernameStatus === 'checking' && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">Checking...</span>
            )}
            {usernameStatus === 'available' && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600 dark:text-green-400">Available</span>
            )}
            {usernameStatus === 'taken' && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-red-600 dark:text-red-400">Taken</span>
            )}
          </div>
          {usernameError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{usernameError}</p>
          )}
          {username && !usernameError && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              Your dizee handle: <strong>{username}</strong>@dizee.com
            </p>
          )}
        </div>

        <div className="flex gap-4 pt-2">
          <button type="button" onClick={() => setStep(OnboardingStep.VerifyOtp)} className="ds-btn ds-btn-secondary flex-1">
            Go back
          </button>
          <button type="submit" disabled={!name.trim() || !isUsernameValid || isCreating} className="ds-btn ds-btn-primary flex-1">
            {isCreating ? 'Creating account…' : 'Continue'}
          </button>
        </div>
      </form>
    </OnboardingShell>
  )
}
